import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDebounceEffect } from "../../hook/useDebounceEffect";
import LeftIcon from "../../images/LeftIcon";
import { webLoadingFalse, webLoadingTrue, webNavigate } from "../../services/navigate.service";
import PopupLocation from "../../components/PopupLocation";
import { getListProduct, } from "../../services/items.services";
import { validateNoSpecialChars, validateOnlyNumbers, } from "../../validation/ValidationInput";
import SearchIcon from "../../images/SearchIcon";
import WhiteCheckIcon from "../../images/WhiteCheckIcon";
import { getListAttributes } from "../../services/attributes.service";
import DropdownIcon from "../../images/DropdownIcon";
import WhiteDropdownIcon from "../../images/WhiteDropdownIcon";
import { createSalePoint, getDetailSalePoint, SalePointPayload, updateSalePoint } from "../../services/salePoint.service";
import { useInfiniteScroll } from "../../hook/useInfiniteScroll";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import { inventoryFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from "../../services/fieldLimits";
import FieldCounter from "../../components/FieldCounter";
import BackIcon from "../../images/BackIcon";
import CheckIcon from "../../images/CheckIcon";
import SortingProduct from "./SortingProduct";
import { useResponsive } from "../../hook/useResponsive";
import SortingAttributes from "./SortingAttributes";
import { useFormKeyboard } from "../../hook/useFormKeyboard";

interface TermsResponse {
    salePointName: string;
    description: string;
    position: number;
}

export interface Product {
    id: string;
    sku: string;
    type: number;
    productName: string;
    productGroup: { groupName: string };
    updatedAt: Date;
    isActive: boolean;
    isAttributes: true | false;
}

interface ProductResponse {
    data: {
        productWithCompany: Product[];
        count: number
    }
}

export interface Attribute {
    id: string;
    attributeName: string;
    displayName: string;
    type: number;
    updatedAt: Date;
    isActive: string;
    isAttributes: true | false;
}
interface AttributesResponse {
    data: {
        data: {
            attributes: Attribute[];
            count: number
        }
    }

}

/** Mô hình chọn sale point — product (bước 2 view tách khỏi mảng này). */
interface ProductAttributesResponse {
    productId: string;
    position: number;
    productName?: string;
    sku?: string;
    type?: number;
    productGroup?: { groupName: string };
    updatedAt?: Date;
    isActive?: boolean;
    salePointId?: string | null;
    salePointProductId?: string | null;
}

interface AttributesSelectResponse {
    attributeId: string;
    position: number;
    attributeName?: string;
    displayName?: string;
    type?: number;
    updatedAt?: Date;
    isActive?: string;
}

function normalizeDetailProductRow(item: any): ProductAttributesResponse {
    const prod = item?.product ?? item;
    const productId = String(item?.productId ?? prod?.id ?? item?.id ?? "");
    return {
        productId,
        position: Number(item?.position ?? 0),
        productName: prod?.productName,
        sku: prod?.sku,
        type: prod?.type,
        productGroup: prod?.productGroup,
        updatedAt: prod?.updatedAt,
        isActive: prod?.isActive,
        salePointId: item?.salePointId ?? null,
        salePointProductId:
            item?.salePointProductId ?? (item?.product != null ? item?.id : null) ?? null,
    };
}

// The step showing each field, so a save error can jump to it.
const SALE_POINT_FIELD_STEPS: Record<string, number> = { salePointName: 1, description: 1, position: 1 };

const SalePointDetail: React.FC = () => {
    const preLanguage = localStorage.getItem("language");
    const limits = useFieldLimits(inventoryFormLimits("sale-point"));
    const initalFilter = {
        searchProduct: "",
        pageProduct: 1,
        searchValue: "",
    }
    const { isMobile, getWidth } = useResponsive();
    const [filter, setFilter] = useState<any>(initalFilter);
    const [listProducts, setListProducts] = useState<ProductResponse['data']['productWithCompany']>([]);
    const [listAttributes, setListAttributes] = useState<AttributesResponse['data']['data']['attributes']>([]);
    const [listRawAttributes, setListRawAttributes] = useState<AttributesResponse['data']['data']['attributes']>([]);
    const [listProductAttributes, setListProductAttributes] = useState<ProductAttributesResponse[]>([]);
    const [listAttributesSelect, setListAttributesSelect] = useState<AttributesSelectResponse[]>([]);
    const listAttributesSelectRef = useRef<AttributesSelectResponse[]>([]);
    listAttributesSelectRef.current = listAttributesSelect;
    const listProductAttributesRef = useRef<ProductAttributesResponse[]>([]);
    listProductAttributesRef.current = listProductAttributes;
    const companyId = localStorage.getItem("companyId") || "";
    const [tab, setTab] = useState<string>("attributes")
    const [filterType, setFilterType] = useState("All");
    const [isFilterTypeDropdownOpen, setIsFilterTypeDropdownOpen] = useState(false);
    const [filterTypeHighlight, setFilterTypeHighlight] = useState(-1);
    const PAGE_SIZE = 20;
    const STEP2_SEARCH_DEBOUNCE_MS = 300;
    const [step, setStep] = useState<number>(1)
    const listTypeFilter = [
        { id: 1, name: "All", value: "All" },
        { id: 2, name: "Checked", value: "Checked" },
    ]
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const filterCorrectionsRef = useRef<HTMLDivElement>(null);
    const [language, setLanguage] = useState<any>(null);
    const [formValues, setFormValues] = useState<TermsResponse>({
        salePointName: '',
        description: '',
        position: 1,
    });
    const [errors, setErrors] = useState<any>({});
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const hasLoadedPageRef = useRef<Set<number>>(new Set());
    /** First page-1 product fetch loads full sale point; later refetches keep local product + attribute order/selection. */
    const hasLoadedFullSalePointDetailRef = useRef(false);
    /** Tránh gọi lại list product/attributes khi bước 2 mở với cùng `search` (đã fetch ở mount hoặc lần trước). */
    const lastProductListSearchKeyRef = useRef<string | null>(null);
    const lastAttributeListSearchKeyRef = useRef<string | null>(null);
    const [hasMore, setHasMore] = useState({
        client: true,
        product: true,
    });
    const [loading, setLoading] = useState({
        client: false,
        product: false,
    });
    const path = window.location.pathname;
    const id: string = path.split('/').pop() || "";
    const scrollProductRef = useRef<HTMLDivElement>(null);
    const kb = useFormKeyboard({ step, autoFocus: id === 'new' && !isMobile });

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterCorrectionsRef.current && !filterCorrectionsRef.current.contains(event.target as Node)) {
                setIsFilterTypeDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    useEffect(() => {
        hasLoadedFullSalePointDetailRef.current = false;
        lastProductListSearchKeyRef.current = null;
        lastAttributeListSearchKeyRef.current = null;
        onLoadMoreProduct();
        if (id === "new") {
            fetchAttributes('');
        }
        const handleNewToken = async () => {
            onLoadMoreProduct();
            if (id === "new") {
                fetchAttributes('');
            }
        };
        document.addEventListener("newToken", handleNewToken);
        return () => {
            document.removeEventListener("newToken", handleNewToken);
        };
    }, [path]);


    const loadInitialSalePointData = async () => {
        webLoadingTrue();
        try {
            const response = await getDetailSalePoint(id, companyId);
            setFormValues({
                salePointName: response.data.data.salePointName,
                description: response.data.data.description,
                position: +response.data.data.position,
            });
            const normalizedProducts = (response.data.data.products || []).map(normalizeDetailProductRow);
            setListProductAttributes(normalizedProducts);
            const normalizedAttributes = (response.data.data.attributes || []).map((a: any) => ({
                ...a,
                attributeId: a.attributeId ?? a.id,
                position: Number(a.position ?? 0),
                attributeName: a.attribute.attributeName ?? a.attribute.displayName,
                displayName: a.attribute.displayName ?? a.attribute.attributeName,
            }));
            setListAttributesSelect(normalizedAttributes);
            if (id !== "new") {
                await fetchAttributes("", normalizedAttributes);
            }
            const details = {
                products: normalizedProducts,
                attributes: normalizedAttributes
            }
            return details
        } catch (error) {
            console.error("Error loading terms:", error);
        } finally {
            webLoadingFalse();
        }
    }

    const fetchAndAppendProductList = async (
        filter: any,
        id: string,
        companyId: string,
        PAGE_SIZE: number,
        isAppend: boolean = true
    ) => {
        try {
            let productSelect: ProductAttributesResponse[] = listProductAttributesRef.current;
            if (filter.pageProduct === 1 && id !== "new") {
                if (!hasLoadedFullSalePointDetailRef.current) {
                    const temp = await loadInitialSalePointData();
                    productSelect = temp?.products ?? listProductAttributesRef.current;
                    hasLoadedFullSalePointDetailRef.current = true;
                }
            }
            const productSearch = String(
                filter.searchValue ?? filter.searchProduct ?? ""
            ).trim();
            const response: ProductResponse = await getListProduct(
                filter.pageProduct,
                PAGE_SIZE,
                productSearch,
                companyId
            );

            const fetchedProducts = response.data.productWithCompany;
            const productSelectIds = new Set(productSelect?.map(p => p.productId) || []);
            const updatedProducts = fetchedProducts.map((value) => ({
                ...value,
                isAttributes: productSelectIds.has(value.id),
            }));
            if (isAppend) {
                setListProducts(prev => [...prev, ...updatedProducts]);
            } else {
                setListProducts(updatedProducts);
            }

            setFilter((prev: any) => ({
                ...prev,
                pageProduct: filter.pageProduct + 1,
                searchValue: filter.searchValue ?? prev.searchValue,
                searchProduct: productSearch,
            }));

            if (fetchedProducts.length < PAGE_SIZE) {
                setHasMore(prev => ({ ...prev, product: false }));
            } else {
                setHasMore(prev => ({ ...prev, product: true }));
            }
            lastProductListSearchKeyRef.current = productSearch;
        } catch (err) {
            console.error("Error fetching product list:", err);
            hasLoadedPageRef.current.delete(filter.pageProduct);
        } finally {
            setLoading(prev => ({ ...prev, product: false }));
        }
    }


    const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
            event.preventDefault();
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const handleFilterTypeSelect = (paymentName: string) => {
        setFilterType(paymentName);
        setIsFilterTypeDropdownOpen(false);
        setFilterTypeHighlight(-1);
    }

    const handleFilterTypeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setIsFilterTypeDropdownOpen(true);
            setFilterTypeHighlight((prev) =>
                e.key === "ArrowDown" ? Math.min(listTypeFilter.length - 1, prev + 1) : Math.max(0, prev - 1)
            );
        } else if ((e.key === "Enter" || e.key === " ") && !isFilterTypeDropdownOpen && !e.ctrlKey && !e.metaKey && !e.altKey && !e.repeat) {
            // A two-option pick list: Enter and Space open it.
            e.preventDefault();
            setIsFilterTypeDropdownOpen(true);
        } else if (e.key === "Enter" && isFilterTypeDropdownOpen && (e.ctrlKey || e.metaKey)) {
            // Not prevented: the chord still saves the form.
            setIsFilterTypeDropdownOpen(false);
            setFilterTypeHighlight(-1);
        } else if (e.key === "Enter" && isFilterTypeDropdownOpen && !e.repeat) {
            e.preventDefault();
            if (filterTypeHighlight !== -1) {
                handleFilterTypeSelect(listTypeFilter[filterTypeHighlight].name);
            } else {
                setIsFilterTypeDropdownOpen(false);
            }
        } else if (e.key === "Tab" && isFilterTypeDropdownOpen) {
            if (filterTypeHighlight !== -1) handleFilterTypeSelect(listTypeFilter[filterTypeHighlight].name);
            else setIsFilterTypeDropdownOpen(false);
        } else if (e.key === "Escape" && isFilterTypeDropdownOpen) {
            e.preventDefault();
            setIsFilterTypeDropdownOpen(false);
            setFilterTypeHighlight(-1);
        }
    };

    // Space toggles, as on a native checkbox; Enter is left to the form.
    const handleCheckKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, value: Product | Attribute, type: string) => {
        if (e.key !== " " || e.ctrlKey || e.metaKey || e.altKey) return;
        e.preventDefault();
        if (!e.repeat) handleSelectProduct(value, type);
    };

    const fetchAttributes = async (searchVal: string, listAttributesResponse?: AttributesSelectResponse[]) => {
        try {
            const response: AttributesResponse = await getListAttributes(
                companyId,
                0,
                0,
                searchVal ?? ""
            );
            const rows = response.data.data.attributes;
            if (listAttributesResponse && listAttributesResponse.length > 0) {
                const updatedProducts = rows.map((value, index) => ({
                    ...value,
                    position: index + 1,
                    isAttributes: listAttributesResponse.some((a) => a.attributeId === value.id),
                }));
                setListAttributes(updatedProducts);
                setListRawAttributes(updatedProducts);
            } else {
                const dataArray = rows.map((val, index) => ({
                    ...val,
                    isAttributes: false,
                    position: index + 1,
                }));
                setListAttributes(dataArray);
                setListRawAttributes(dataArray);
            }
            lastAttributeListSearchKeyRef.current = String(searchVal ?? "").trim();
        } catch (error) {
            console.error("Error loading terms:", error);
        } finally {
        }
    };

    useDebounceEffect(
        () => {
            if (step !== 2 || tab !== "attributes") return;
            const kw = filter.searchValue?.trim() ?? "";
            const merge =
                listAttributesSelectRef.current.length > 0 ? listAttributesSelectRef.current : undefined;
            if (
                lastAttributeListSearchKeyRef.current === kw &&
                listAttributes.length > 0
            ) {
                if (merge && merge.length > 0) {
                    const selIds = new Set(merge.map((a) => a.attributeId));
                    setListAttributes((prev) =>
                        prev.map((row) => ({ ...row, isAttributes: selIds.has(row.id) }))
                    );
                    setListRawAttributes((prev) =>
                        prev.map((row) => ({ ...row, isAttributes: selIds.has(row.id) }))
                    );
                } else {
                    setListAttributes((prev) =>
                        prev.map((row) => ({ ...row, isAttributes: false }))
                    );
                    setListRawAttributes((prev) =>
                        prev.map((row) => ({ ...row, isAttributes: false }))
                    );
                }
                return;
            }
            void fetchAttributes(kw, merge);
        },
        [filter.searchValue, tab, step, id],
        STEP2_SEARCH_DEBOUNCE_MS
    );

    const onLoadMoreProduct = async () => {
        if (hasLoadedPageRef.current.has(filter.pageProduct)) return;
        hasLoadedPageRef.current.add(filter.pageProduct);
        setLoading(prev => ({ ...prev, product: true }));
        await fetchAndAppendProductList(
            filter,
            id,
            companyId,
            PAGE_SIZE,
            true
        );
    };

    const { setLoaderRef } = useInfiniteScroll({
        hasMore: hasMore.product,
        loading: loading.product,
        onLoadMore: onLoadMoreProduct,
        scrollRootRef: scrollProductRef,
    });

    useEffect(() => {
        if (window.languageContext) {
            setLanguage(window.languageContext.language);
        }
    }, [window, window.languageContext]);
    const handleClosePopup = () => {
        setShowPopup(false);
    };
    const handleOpenPopup = () => {
        setShowPopup(true);
    };

    const handleSelectProduct = (value: Product | Attribute, type: string) => {
        if (type === "items") {
            const p = value as Product;
            const exists = listProductAttributes.some((attr) => attr.productId === p.id);
            let updatedRows: ProductAttributesResponse[];
            if (exists) {
                updatedRows = listProductAttributes
                    .filter((attr) => attr.productId !== p.id)
                    .map((row, i) => ({ ...row, position: i + 1 }));
            } else {
                const maxPos = listProductAttributes.reduce(
                    (m, r) => Math.max(m, Number(r.position) || 0),
                    0
                );
                updatedRows = [
                    ...listProductAttributes,
                    {
                        productId: p.id,
                        position: maxPos + 1,
                        productName: p.productName,
                        sku: p.sku,
                        type: p.type,
                        productGroup: p.productGroup,
                        updatedAt: p.updatedAt,
                        isActive: p.isActive,
                    },
                ];
            }
            setListProductAttributes(updatedRows);
            const sel = new Set(updatedRows.map((x) => x.productId));
            setListProducts((prev) => prev.map((row) => ({ ...row, isAttributes: sel.has(row.id) })));
        } else {
            const a = value as Attribute;
            const exists = listAttributesSelect.some((attr) => attr.attributeId === a.id);
            let updatedSel: AttributesSelectResponse[];
            if (exists) {
                updatedSel = listAttributesSelect
                    .filter((attr) => attr.attributeId !== a.id)
                    .map((row, i) => ({ ...row, position: i + 1 }));
            } else {
                const maxPos = listAttributesSelect.reduce(
                    (m, r) => Math.max(m, Number(r.position) || 0),
                    0
                );
                updatedSel = [
                    ...listAttributesSelect,
                    {
                        attributeId: a.id,
                        position: maxPos + 1,
                        attributeName: a.attributeName,
                        displayName: a.displayName,
                        type: a.type,
                        updatedAt: a.updatedAt,
                        isActive: a.isActive,
                    },
                ];
            }
            setListAttributesSelect(updatedSel);
            const selIds = new Set(updatedSel.map((x) => x.attributeId));
            setListAttributes((prev) => prev.map((row) => ({ ...row, isAttributes: selIds.has(row.id) })));
            setListRawAttributes((prev) => prev.map((row) => ({ ...row, isAttributes: selIds.has(row.id) })));
        }
    };

    const loadDataProduct = async (partial: Record<string, any>) => {
        hasLoadedPageRef.current.clear();
        const merged = { ...filter, ...partial };
        await fetchAndAppendProductList(
            merged,
            id,
            companyId,
            PAGE_SIZE,
            false,
        );
    };

    useDebounceEffect(
        () => {
            if (step !== 2 || tab !== "items") return;
            const kw = filter.searchValue?.trim() ?? "";
            if (
                lastProductListSearchKeyRef.current === kw &&
                listProducts.length > 0
            ) {
                const sel = new Set(listProductAttributesRef.current.map((r) => r.productId));
                setListProducts((prev) =>
                    prev.map((p) => ({ ...p, isAttributes: sel.has(p.id) }))
                );
                return;
            }
            void loadDataProduct({
                pageProduct: 1,
                searchValue: kw,
                searchProduct: kw,
            });
        },
        [filter.searchValue, tab, step, id],
        STEP2_SEARCH_DEBOUNCE_MS
    );

    const handleSubmitSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Enter" || event.ctrlKey || event.metaKey) return;
        event.preventDefault();
        if (tab === "attributes") {
            const kw = filter.searchValue?.trim() ?? "";
            const merge =
                listAttributesSelectRef.current.length > 0 ? listAttributesSelectRef.current : undefined;
            void fetchAttributes(kw, merge);
        } else {
            const kw = filter.searchValue?.trim() ?? "";
            void loadDataProduct({
                pageProduct: 1,
                searchValue: kw,
                searchProduct: kw,
            });
        }
    };
    const handleSubmit = async (e: React.SyntheticEvent) => {
        e.preventDefault();
        const newErrors: any = validateAgainstLimits(formValues as any, limits, language, ["salePointName", "description"]);
        if (!formValues.salePointName) {
            newErrors.salePointName = 'Sale point name is required';
        }
        if (!formValues.position) {
            newErrors.position = 'Position is required';
        }
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            setStep(1);
            return;
        }

        webLoadingTrue();
        const payload: SalePointPayload = {
            ...formValues,
            position: Number(formValues.position),
            companyId,
            products: listProductAttributes
                .filter((attr) => attr.productId)
                .map((attr) => ({
                    productId: attr.productId,
                    position: Number(attr.position) || 0,
                })),
            attributes: listAttributesSelect
                .filter((attr) => attr.attributeId)
                .map((attr) => ({
                    attributeId: attr.attributeId,
                    position: Number(attr.position) || 0,
                })),
        };
        if (id === "new") {
            try {
                const response: any = await createSalePoint(payload);
                if (response) {
                    webNavigate("/" + preLanguage + "/salePoint");
                    webLoadingFalse();
                    showToast({
                        message: "Sale Point Created !",
                        type: 'success',
                        duration: 20000,
                    });
                }
            } catch (error: any) {
                webLoadingFalse();
                showApiError(error, { language, setFieldErrors: setErrors, fieldSteps: SALE_POINT_FIELD_STEPS, onStep: setStep });

            }
        } else {
            try {
                const response: any = await updateSalePoint(id, companyId, payload);
                if (response) {
                    webNavigate("/" + preLanguage + "/salePoint");
                    webLoadingFalse();
                    showToast({
                        message: language?.['Sale Point Updated !'] ?? 'Sale Point Updated !',
                        type: 'success',
                        duration: 20000,
                    });
                }
            } catch (error: any) {
                webLoadingFalse();
                showApiError(error, { language, setFieldErrors: setErrors, fieldSteps: SALE_POINT_FIELD_STEPS, onStep: setStep });
            }
        }
    };

    const handleSortingProduct = (newList: Product[]) => {
        setListProductAttributes((prev) =>
            newList.map((item, index) => {
                const existing = prev.find((p) => p.productId === item.id);
                return {
                    ...existing,
                    productId: item.id,
                    position: index + 1,
                };
            })
        );
    };

    const handleSortingAttribute = (newList: Attribute[]) => {
        setListAttributesSelect((prev) =>
            newList.map((item, index) => {
                const existing = prev.find((a) => a.attributeId === item.id);
                return {
                    ...existing,
                    attributeId: item.id,
                    position: index + 1,
                };
            })
        );
    };

    /** Bước 3: chỉ từ mô hình chọn (listProductAttributes), không phụ thuộc trang search bước 2. */
    const sortedProductsForSortingStep = useMemo(() => {
        return [...listProductAttributes]
            .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0))
            .map((row) => ({
                id: row.productId,
                productName: row.productName ?? "",
                sku: row.sku ?? "",
                type: row.type ?? 0,
                productGroup: row.productGroup ?? { groupName: "" },
                updatedAt: row.updatedAt ?? new Date(),
                isActive: row.isActive ?? true,
                isAttributes: true as const,
            }));
    }, [listProductAttributes]);

    const sortedAttributesForSortingStep = useMemo(() => {
        return [...listAttributesSelect]
            .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0))
            .map((row) => ({
                id: row.attributeId,
                attributeName: row.attributeName ?? "",
                displayName: row.displayName ?? row.attributeName ?? "",
                type: row.type ?? 0,
                updatedAt: row.updatedAt ?? new Date(),
                isActive: row.isActive ?? "true",
                isAttributes: true as const,
            }));
    }, [listAttributesSelect]);

    const handleChangeStep = () => {
        const attrSel = listAttributesSelectRef.current;
        const attrIds = new Set(attrSel.map((a) => a.attributeId));
        setListAttributes((prev) =>
            prev.map((row) => ({ ...row, isAttributes: attrIds.has(row.id) }))
        );
        setListRawAttributes((prev) =>
            prev.map((row) => ({ ...row, isAttributes: attrIds.has(row.id) }))
        );
        const productIds = new Set(listProductAttributesRef.current.map((r) => r.productId));
        setListProducts((prev) =>
            prev.map((p) => ({ ...p, isAttributes: productIds.has(p.id) }))
        );
        setStep(3);
    };

    /** Step 3 → 2 → 1; Step 1 → danh sách Items (đồng bộ header + footer Cancel/Back). */
    const handleBackNavigation = () => {
        if (step === 1) {
            webNavigate('/' + preLanguage + '/items' + '?tab=Items');
        } else if (step === 2) {
            setStep(1);
        } else {
            setStep(2);
        }
    };

    return (
        <div {...kb.scopeProps}>
            <div className="bg-white">
                <button type="button" className="flex items-center cursor-pointer" {...kb.stepProps(handleBackNavigation)}>
                    <div
                        className="w-6 h-6 flex items-center justify-center"
                    >
                        <BackIcon />
                    </div>
                    <p
                        style={{ fontSize: '14px', fontWeight: '500', lineHeight: '28px', color: '#222525' }}
                        className="font-inter"
                    >
                        {language && language?.['Back'] ? language?.['Back'] : 'Back'}
                    </p>
                </button>
            </div>
            {id === 'new' ? (
                <p
                    style={{
                        margin: '20px 0px',
                        fontSize: '20px',
                        fontWeight: '500',
                        lineHeight: '28px',
                        color: '#222525',
                    }}
                    className="font-inter"
                >
                    {language && language?.['Add Sale Point']
                        ? language?.['Add Sale Point']
                        : 'Add Sale Point'}
                </p>
            ) : (
                <p
                    style={{
                        margin: '20px 0px',
                        fontSize: '20px',
                        fontWeight: '500',
                        lineHeight: '28px',
                        color: '#222525',
                    }}
                    className="font-inter"
                >
                    {language && language?.['Edit Sale Point']
                        ? language?.['Edit Sale Point']
                        : 'Edit Sale Point'}
                </p>
            )}
            <div className="flex items-start lg:items-center" style={{ gap: isMobile ? '10px' : '20px', marginTop: isMobile ? '2px' : '0' }}>
                <div className="" style={{ width: isMobile ? 'calc(33.333% - 7px)' : 'calc(33% - 10px)' }}>
                    <div
                        className=""
                        style={{
                            minHeight: '4px',
                            width: "100%",
                            backgroundColor: (step === 1 || step === 2 || step === 3) ? '#0F7679' : '#DBE4E4',
                            borderRadius: '20px',
                        }}
                    ></div>
                    <div className="" style={{ marginTop: isMobile ? "6px" : "12px" }}>
                        <div className="flex items-center" style={{ gap: '8px' }}>
                            {step === 2 && (
                                <div
                                    className="flex items-center justify-center"
                                    style={{ width: '20px', height: '20px' }}
                                >
                                    <CheckIcon />
                                </div>
                            )}
                            <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: '#0F7679' }}
                            >
                                {language && language?.['Step 1'] ? language?.['Step 1'] : 'Step 1'}
                            </p>
                        </div>
                        <p
                            className="text-[14px] font-inter leading-[20px] font-medium"
                            style={{ color: '#05292A' }}
                        >
                            {language && language?.['Details'] ? language?.['Details'] : 'Details'}
                        </p>
                    </div>
                </div>
                <div className="" style={{ width: isMobile ? 'calc(33.333% - 7px)' : 'calc(33% - 10px)' }}>
                    <div
                        className=""
                        style={{
                            minHeight: '4px',
                            width: "100%",
                            backgroundColor: (step === 2 || step === 3) ? '#0F7679' : '#DBE4E4',
                            borderRadius: '20px',
                        }}
                    ></div>
                    <div className="" style={{ marginTop: isMobile ? "6px" : "12px" }}>
                        <div className="flex items-center">
                            <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: (step === 2 || step === 3) ? '#0F7679' : '#819596' }}
                            >
                                {language && language?.['Step 2'] ? language?.['Step 2'] : 'Step 2'}
                            </p>
                        </div>
                        <p
                            className="text-[14px] font-inter leading-[20px] font-medium"
                            style={{ color: '#05292A' }}
                        >
                            {language && language?.['Items & Attributes']
                                ? language?.['Items & Attributes']
                                : 'Items & Attributes'}
                        </p>
                    </div>
                </div>
                <div className="" style={{ width: isMobile ? 'calc(33.333% - 7px)' : 'calc(33% - 10px)' }}>
                    <div
                        className=""
                        style={{
                            minHeight: '4px',
                            width: "100%",
                            backgroundColor: step === 3 ? '#0F7679' : '#DBE4E4',
                            borderRadius: '20px',
                        }}
                    ></div>
                    <div className="" style={{ marginTop: isMobile ? "6px" : "12px" }}>
                        <div className="flex items-center">
                            <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: (step === 3) ? '#0F7679' : '#819596' }}
                            >
                                {language && language?.['Step 3'] ? language?.['Step 3'] : 'Step 3'}
                            </p>
                        </div>
                        <p
                            className="text-[14px] font-inter leading-[20px] font-medium"
                            style={{ color: '#05292A' }}
                        >
                            {language && language?.['Sorting']
                                ? language?.['Sorting']
                                : 'Sorting'}
                        </p>
                    </div>
                </div>
            </div>
            <div
                className="scroll-container"
                style={{ height: isMobile ? 'calc(100% - 264px)' : 'calc(100% - 219px)', paddingBottom: isMobile ? '16px' : '0px' }}
            >
                {step === 1 ?
                    <form>
                        <div className="rounded-md bg-white flex items-start " style={{ marginTop: "24px", flexWrap: "wrap", gap: 20 }}>
                            <div style={{ width: isMobile ? "100%" : 'calc(49% - 10px)' }}>
                                <div className="space-y-2">
                                    <div className="flex items-center">
                                        <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#4A5353' }}>{language && language?.["Sale point name"] ? language?.["Sale point name"] : "Sale point name"}</p>
                                        <p
                                            className="text-[14px] font-inter leading-[20px] font-medium"
                                            style={{ color: '#BA0E13' }}
                                        >
                                            {language && language?.['*'] ? language?.['*'] : '*'}
                                        </p>
                                    </div>
                                    <div className="w-full">
                                        <input
                                            required
                                            type="text"
                                            name="salePointName"
                                            {...limitProps(limits, "salePointName")}
                                            value={formValues.salePointName}
                                            onChange={handleChange}
                                            onKeyDown={validateNoSpecialChars}
                                            className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                                        />
                                        <FieldCounter value={formValues.salePointName} max={limits.salePointName?.maxLength} />
                                    </div>
                                    {errors.salePointName && <p className="error">{errors.salePointName}</p>}
                                </div>
                            </div>
                            <div style={{ width: isMobile ? "100%" : 'calc(49% - 10px)' }}>
                                <div className="space-y-2">
                                    <div className="flex items-center">
                                        <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#4A5353' }}>{language && language?.["Position"] ? language?.["Position"] : "Position"}</p>
                                        <p
                                            className="text-[14px] font-inter leading-[20px] font-medium"
                                            style={{ color: '#BA0E13' }}
                                        >
                                            {language && language?.['*'] ? language?.['*'] : '*'}
                                        </p>
                                    </div>
                                    <div className="w-full">
                                        <input
                                            type="text"
                                            name="position"
                                            value={formValues.position} // Use position instead of formValues?.position
                                            onChange={handleChange}
                                            onKeyDown={validateOnlyNumbers}
                                            className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div style={{ width: "100%" }}>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#4A5353' }}>{language && language?.["Description"] ? language?.["Description"] : "Description"}</p>
                                    </div>
                                    <div className="w-full">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                name="description"
                                                {...limitProps(limits, "description")}
                                                value={formValues.description}
                                                onChange={handleChange}
                                                className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                                            />
                                            <FieldCounter value={formValues.description} max={limits.description?.maxLength} />
                                        </div>
                                    </div>
                                    {errors.description && <p className="error">{errors.description}</p>}
                                </div>
                            </div>
                        </div>
                    </form>
                    : step === 2 ?
                        <>
                            <div style={{ margin: '24px 0px 20px 0px' }}>
                                <div className="flex items-center justify-between">
                                    <div className="flex item-cemter" style={{ gap: '12px', }}>
                                        <button
                                            type="button"
                                            aria-pressed={tab === "attributes"}
                                            style={{ color: tab === "attributes" ? "#FFFFFF" : '#6A7878', padding: "6px 14px", borderRadius: "30px", backgroundColor: tab === "attributes" ? '#0D5759' : 'transparent', border: tab === "attributes" ? '1px solid transparent' : '1px solid #C9D7D8' }}
                                            onClick={() => setTab("attributes")}
                                            className="cursor-pointer text-[14px] leading-[130%] font-medium font-inter"
                                        >
                                            {language && language?.["Attributes"] ? language?.["Attributes"] : "Attributes"}
                                        </button>
                                        <button
                                            type="button"
                                            aria-pressed={tab === "items"}
                                            style={{ color: tab === "items" ? "#FFFFFF" : '#6A7878', padding: "6px 14px", borderRadius: "30px", backgroundColor: tab === "items" ? '#0D5759' : 'transparent', border: tab === "items" ? '1px solid transparent' : '1px solid #C9D7D8' }}
                                            onClick={() => setTab("items")}
                                            className="cursor-pointer text-[14px] leading-[130%] font-medium font-inter"
                                        >
                                            {language && language?.["Items"] ? language?.["Items"] : "Items"}
                                        </button>
                                    </div>
                                    <div className="flex items-center" style={{ gap: '16px' }}>
                                        {
                                            !isMobile &&
                                            (<div className="flex items-center gap-x-2">
                                                <div className="relative w-[400px] h-10">
                                                    <input
                                                        ref={searchInputRef}
                                                        style={{
                                                            borderColor: '#E7F1F1',
                                                            padding: '8px 36px',
                                                            minWidth: '332px',
                                                            borderRadius: '12px',
                                                            border: '1px solid #C9D7D8',
                                                            color: '#05292A',
                                                        }}
                                                        name="searchValue"
                                                        onKeyDown={handleSubmitSearch}
                                                        value={filter.searchValue}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFilter((prev: any) => ({
                                                                ...prev,
                                                                searchValue: e.target.value,
                                                            }));
                                                        }}
                                                        className="text-[14px] font-inter w-full h-full rounded-md outline-none focus:none bg-white bg-transparent"
                                                    />
                                                    <div className="absolute top-1/2 -translate-y-1/2" style={{ left: '8px' }}>
                                                        <SearchIcon />
                                                    </div>
                                                </div>
                                            </div>)
                                        }
                                        <div className="flex items-center cursor-pointer" style={{ gap: 8 }}>
                                            <div className="flex items-center" style={{ borderRadius: "12px", border: '1px solid #DBE4E4', padding: "8px 8px 8px 12px", height: 40, width: 120 }}>
                                                <div className="relative" ref={filterCorrectionsRef}>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            value={filterType}
                                                            readOnly
                                                            style={{ color: "#6A7878", fontSize: 14, fontWeight: '500', padding: 0 }}
                                                            onClick={() => setIsFilterTypeDropdownOpen(true)}
                                                            onKeyDown={handleFilterTypeKeyDown}
                                                            role="combobox"
                                                            aria-expanded={isFilterTypeDropdownOpen}
                                                            aria-haspopup="listbox"
                                                            aria-label={language?.['Filter'] ?? 'Filter'}
                                                            className="font-inter placeholder:text-placeholder text-main p-3 outline-none focus-none bg-transparent w-full"
                                                        />
                                                        <div
                                                            className="absolute flex items-center justify-center w-6 h-6"
                                                            style={{ top: "50%", right: 0, transform: "translateY(-50%)" }}
                                                        >
                                                            {filterType === "All" ?
                                                                <DropdownIcon />
                                                                :
                                                                <WhiteDropdownIcon />
                                                            }
                                                        </div>
                                                    </div>
                                                    {isFilterTypeDropdownOpen && (
                                                        <div
                                                            role="listbox"
                                                            style={{ width: "100px", left: '-10px', top: '30px' }}
                                                            className="absolute z-[100] mt-1 bg-white border border-[#EBEBED] rounded-lg max-h-60 overflow-auto p-1 shadow-extend">
                                                            {listTypeFilter
                                                                .map((status: any, statusIndex: number) => (
                                                                    <div
                                                                        key={status?.id}
                                                                        role="option"
                                                                        aria-selected={filterType === status?.name}
                                                                        style={{ padding: "8px 6px", borderRadius: "4px", color: "#6A7878", fontSize: 14, fontWeight: '500', ...(filterTypeHighlight === statusIndex ? { backgroundColor: '#E7F1F1' } : {}) }}
                                                                        className="cursor-pointer hover:bg-[#E7F1F1] text-main text-[14px] font-inter duration-300"
                                                                        onMouseDown={(e) => e.preventDefault()}
                                                                        onClick={() => handleFilterTypeSelect(status?.name)}
                                                                    >
                                                                        {language?.[status?.name] ?? status?.name}
                                                                    </div>
                                                                ))
                                                            }
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {filter.searchValue !== '' &&
                                                <button type="button" onClick={() => {
                                                    if (tab === "attributes") {
                                                        const merge =
                                                            listAttributesSelectRef.current.length > 0
                                                                ? listAttributesSelectRef.current
                                                                : undefined;
                                                        void fetchAttributes("", merge);
                                                    } else {
                                                        void loadDataProduct({
                                                            pageProduct: 1,
                                                            searchValue: "",
                                                            searchProduct: "",
                                                        });
                                                    }
                                                    setFilter((prev: any) => ({
                                                        ...prev,
                                                        searchValue: "",
                                                        searchProduct: "",
                                                    }));
                                                }} className="flex items-center justify-center font-inter cursor-pointer" style={{ width: 100, height: 36, fontSize: 14, color: "#6E6E6E" }}>
                                                    <p>{language?.['Clear filters'] ?? 'Clear filters'}</p>
                                                </button>
                                            }
                                        </div>
                                    </div>
                                </div>
                                {
                                    isMobile &&
                                    (<div className="flex items-center gap-x-2" style={{ marginTop: '12px' }}>
                                        <div className="relative w-[400px] h-10">
                                            <input
                                                ref={searchInputRef}
                                                style={{
                                                    borderColor: '#E7F1F1',
                                                    padding: '8px 36px',
                                                    minWidth: '332px',
                                                    borderRadius: '12px',
                                                    border: '1px solid #C9D7D8',
                                                    color: '#05292A',
                                                }}
                                                name="searchValue"
                                                onKeyDown={handleSubmitSearch}
                                                value={filter.searchValue}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setFilter((prev: any) => ({
                                                        ...prev,
                                                        searchValue: e.target.value,
                                                    }));
                                                }}
                                                className="text-[14px] font-inter w-full h-full rounded-md outline-none focus:none bg-white bg-transparent"
                                            />
                                            <div className="absolute top-1/2 -translate-y-1/2" style={{ left: '8px' }}>
                                                <SearchIcon />
                                            </div>
                                        </div>
                                    </div>)
                                }
                            </div>
                            {
                                tab === "attributes" ?
                                    <div className="h-full w-full flex bg-white scroll-container" style={{ gap: isMobile ? "12px" : '20px', overflowY: "scroll", flexWrap: 'wrap', height: "calc(100% - 103px)" }}>
                                        {listAttributes
                                            .filter((val) => filterType === "All" || val.isAttributes)
                                            .map((value, index) => {
                                                return (
                                                    <div
                                                        key={value.id}
                                                        className=""
                                                        style={{
                                                            width: !isMobile ? "calc(24% - 10px)" : 'calc(50% - 6px)',
                                                            border: value.isAttributes ? '2px solid #0F7679' : '1px solid #EBF0F0',
                                                            borderRadius: 16,
                                                            padding: '16px',
                                                            backgroundColor: value.isAttributes ? '#0F767914' : '#F8F8F8',
                                                            height: '108px',
                                                        }}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div
                                                                role="checkbox"
                                                                aria-checked={!!value.isAttributes}
                                                                aria-label={value.attributeName}
                                                                tabIndex={0}
                                                                onKeyDown={(e) => handleCheckKeyDown(e, value, "attributes")}
                                                                className="cursor-pointer w-5 h-5 flex items-center justify-center"
                                                                onClick={() => { handleSelectProduct(value, "attributes") }}
                                                                style={{
                                                                    border: value.isAttributes ? '' : '1px solid #C9D7D8',
                                                                    backgroundColor: value.isAttributes ? '#0F7679' : 'transparent',
                                                                    borderRadius: '6px',
                                                                }}
                                                            >
                                                                {value.isAttributes ? <WhiteCheckIcon /> : <></>}
                                                            </div>
                                                            <div
                                                                className="flex items-center gap-x-2 font-inter font-medium"
                                                                style={{
                                                                    fontSize: '12px',
                                                                    borderRadius: '100px',
                                                                    height: '26px',
                                                                    padding: '5px 8px',
                                                                    color: '#007C46',
                                                                    backgroundColor: '#007C461F',
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: '6px',
                                                                        height: '6px',
                                                                        borderRadius: '100%',
                                                                        backgroundColor: '#007C46',
                                                                    }}
                                                                ></div>
                                                                {value.isActive ? 'Active' : 'Inactive'}
                                                            </div>
                                                        </div>
                                                        <div className="space-y-1" style={{ marginTop: '12px' }}>
                                                            <p
                                                                className="font-inter"
                                                                style={{
                                                                    fontSize: '16px',
                                                                    lineHeight: '20px',
                                                                    color: '#222525',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {value.attributeName}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                    </div>
                                    :
                                    <div ref={scrollProductRef} className="h-full w-full flex bg-white scroll-container" style={{ gap: isMobile ? "12px" : '20px', overflowY: "scroll", flexWrap: 'wrap', height: "calc(100% - 103px)" }}>
                                        {listProducts
                                            .filter((val) => filterType === "All" || val.isAttributes)
                                            .map((value, index) => {
                                                return (
                                                    <div
                                                        key={value.id}
                                                        className=""
                                                        style={{
                                                            width: !isMobile ? "calc(24% - 10px)" : 'calc(50% - 6px)',
                                                            border: value.isAttributes ? '2px solid #0F7679' : '1px solid #EBF0F0',
                                                            borderRadius: 16,
                                                            padding: '16px',
                                                            backgroundColor: value.isAttributes ? '#0F767914' : '#F8F8F8',
                                                            height: '108px',
                                                        }}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div
                                                                role="checkbox"
                                                                aria-checked={!!value.isAttributes}
                                                                aria-label={value.productName}
                                                                tabIndex={0}
                                                                onKeyDown={(e) => handleCheckKeyDown(e, value, "items")}
                                                                className="cursor-pointer w-5 h-5 flex items-center justify-center"
                                                                onClick={() => { handleSelectProduct(value, "items") }}
                                                                style={{
                                                                    border: value.isAttributes ? '' : '1px solid #C9D7D8',
                                                                    backgroundColor: value.isAttributes ? '#0F7679' : 'transparent',
                                                                    borderRadius: '6px',
                                                                }}
                                                            >
                                                                {value.isAttributes ? <WhiteCheckIcon /> : <></>}
                                                            </div>
                                                            <div
                                                                className="flex items-center gap-x-2 font-inter font-medium"
                                                                style={{
                                                                    fontSize: '12px',
                                                                    borderRadius: '100px',
                                                                    height: '26px',
                                                                    padding: '5px 8px',
                                                                    color: '#007C46',
                                                                    backgroundColor: '#007C461F',
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: '6px',
                                                                        height: '6px',
                                                                        borderRadius: '100%',
                                                                        backgroundColor: '#007C46',
                                                                    }}
                                                                ></div>
                                                                {value.isActive ? 'Active' : 'Inactive'}
                                                            </div>
                                                        </div>
                                                        <div className="space-y-1" style={{ marginTop: '12px' }}>
                                                            <p
                                                                className="font-inter"
                                                                style={{
                                                                    fontSize: '16px',
                                                                    lineHeight: '20px',
                                                                    color: '#222525',
                                                                    fontWeight: 600,
                                                                }}
                                                            >{value.productName}</p>
                                                            <p className="font-inter" style={{ fontSize: "14px", lineHeight: "18px", color: "#4A5353" }}>{value.sku}</p>
                                                        </div>

                                                    </div>
                                                )
                                            })}
                                        <div ref={setLoaderRef} style={{ height: "1px" }}>
                                            {loading.product &&
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <span className="loader"></span>
                                                </div>
                                            }
                                        </div>
                                    </div>

                            }
                        </>
                        :
                        <div className="w-full h-full" style={{ margin: '24px 0px 20px 0px' }}>
                            {sortedProductsForSortingStep.length > 0 && (
                                <SortingProduct
                                    language={language}
                                    handleChangePositionProduct={handleSortingProduct}
                                    product={sortedProductsForSortingStep}
                                />
                            )}
                            {sortedAttributesForSortingStep.length > 0 && (
                                <SortingAttributes
                                    language={language}
                                    handleChangePositionAttribute={handleSortingAttribute}
                                    product={sortedAttributesForSortingStep}
                                />
                            )}
                        </div>
                }
            </div>
            <div
                className="w-full flex items-center justify-end shadow_button relative"
                style={{ gap: "12px", paddingTop: "24px", }}
            >
                <div className="absolute" style={{ left: "-24px", top: 0, height: "1px", backgroundColor: "#DBE4E4", width: isMobile ? 'calc(100vw + 32px)' : "calc(-256px + 48px + 100vw)" }}></div>
                <button
                    type="button"
                    {...kb.stepProps(handleBackNavigation)}
                    style={{
                        padding: '10px 24px',
                        borderRadius: '30px',
                        color: '#222525',
                        fontSize: '14px',
                        fontWeight: '500',
                        border: '1px solid #C9D7D8',
                    }}
                    className="font-inter cursor-pointer"
                >
                    {step === 1 ?
                        language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'
                        :
                        language && language?.['Back'] ? language?.['Back'] : 'Back'
                    }
                </button>
                {step < 3 ? (
                    <button
                        type="button"
                        {...kb.primaryProps(() => {
                            step === 1 ? setStep(2) : handleChangeStep()
                        })}
                        style={{
                            padding: '10px 24px',
                            borderRadius: '30px',
                            color: '#FFFFFF',
                            fontSize: '14px',
                            fontWeight: '500',
                            backgroundColor: '#0F7679',
                        }}
                        className="font-inter cursor-pointer"
                    >
                        {language && language?.['Next'] ? language?.['Next'] : 'Next'}
                    </button>
                ) : (
                    <button
                        type="button"
                        {...kb.primaryProps(handleSubmit)}
                        style={{
                            padding: '10px 24px',
                            borderRadius: '30px',
                            color: '#FFFFFF',
                            fontSize: '14px',
                            fontWeight: '500',
                            backgroundColor: '#0F7679',
                        }}
                        className="font-inter cursor-pointer"
                    >
                        {language && language?.['Save changes'] ? language?.['Save changes'] : 'Save changes'}
                    </button>
                )}
            </div>
            <PopupLocation show={showPopup} title={`${id === 'new' ? (language && language?.["Create Sale Point"]) ? language?.["Create Sale Point"] : "Create Sale Point" : language && language?.["Update Sale Point"] ? language?.["Update Sale Point"] : "Update Sale Point"}`} desc={`${id === 'new' ? (language && language?.["Create Sale Point Failed"]) ? language?.["Create Sale Point Failed"] : "Create Sale Point Failed" : language && language?.["Update Sale Point Failed"] ? language?.["Update Sale Point Failed"] : "Update Sale Point Failed"}`} onClose={handleClosePopup} onOpen={handleOpenPopup} />
        </div>
    );
};

export default SalePointDetail;
